import React, { useEffect } from "react";
import data from "../content/Products.json";
import PageNav from "../components/PageNav";
import { useLocation } from "react-router-dom";
import PageIntro from "../components/PageIntro";

const slugify = (value = "") =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");

const ProductContent = ({ item, isSubsection = false }) => {
  const HeadingTag = isSubsection ? "h3" : "h2";

  return (
    <div
      className={
        isSubsection
          ? "bg-white3 rounded-[28px] border border-white2 p-6 space-y-6 shadow"
          : "space-y-6"
      }
    >
      <div>
        <HeadingTag
          className={
            isSubsection
              ? "text-2xl font-semibold text-red"
              : "text-3xl font-semibold text-red"
          }
        >
          {item.title}
        </HeadingTag>
        <div className="border-box mt-1"></div>
        {item.content && (
          <p
            className="text-justify text-gray-700 mt-3"
            dangerouslySetInnerHTML={{ __html: item.content }}
          ></p>
        )}
      </div>
      {item.uiType === "table" ? (
        <div className="bg-white3 rounded-2xl shadow p-5 overflow-x-auto">
          <table className="w-full rounded-2xl border border-[#8b8b8b] text-sm">
            <thead>
              <tr className="bg-gray-100">
                {item.list.map((col) => (
                  <th
                    key={col.id}
                    className="border border-[#8b8b8b] px-4 py-2 text-left font-semibold"
                  >
                    {col.title}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="rounded-2xl border">
              {item.list[0].value.map((_, rowIndex) => (
                <tr key={rowIndex}>
                  {item.list.map((col, colIndex) => (
                    <td
                      key={colIndex}
                      className="rounded-2xl border border-[#8b8b8b] px-2 py-1 sm:px-4 sm:py-2 text-sm sm:text-base"
                    >
                      {col.value[rowIndex]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        /* Default card/grid layout for other items */
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {item.list.map((detail, index) => (
            <div
              key={detail.id || `${detail.title}-${index}`}
              className="bg-white3 rounded-2xl shadow p-5"
            >
              <h4 className="text-base font-semibold text-red mb-2">
                {detail.title}
              </h4>

              <ul className="text-gray-600 space-y-1">
                {detail.value.map((val, idx) => (
                  <li key={idx}>{val}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {(item.Terms || item.terms) && (
        <div className="space-y-4">
          {(item.Terms || item.terms).map(
            (term, idx) =>
              term.title && (
                <div key={idx} className="bg-white3 p-6 rounded-2xl shadow">
                  <h3 className="text-2xl font-semibold mb-3 text-primary">
                    {term.title}
                  </h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    {term.list.map((entry, liIndex) => (
                      <li key={liIndex}>{entry}</li>
                    ))}
                  </ul>
                </div>
              ),
          )}
        </div>
      )}
    </div>
  );
};

const ExtraSection = ({ data }) => (
  <div className="bg-white3 rounded-[28px] border border-white2 p-6 shadow space-y-4">
    <h3 className="text-2xl font-semibold text-primary">{data.title}</h3>
    <ul className="list-disc list-inside space-y-2 text-gray-700">
      {data.items.map((item, idx) => (
        <li key={idx}>{item}</li>
      ))}
    </ul>
  </div>
);

const Products = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const element = document.getElementById(id);

      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 200);
      }
    }
  }, [location]);

  const navItems = data.page.flatMap((section) => {
    const sectionId = slugify(section.title);
    const entries = [{ title: section.title, id: sectionId }];
    if (section.subsections) {
      section.subsections.forEach((sub) => {
        entries.push({
          title: `${section.title} - ${sub.title}`,
          id: `${sectionId}-${slugify(sub.title)}`,
        });
      });
    }
    if (section.extraSections) {
      section.extraSections.forEach((extra) => {
        entries.push({
          title: `${section.title} - ${extra.title}`,
          id: `${sectionId}-${slugify(extra.title)}`,
        });
      });
    }
    return entries;
  });

  const navData = { page: navItems };

  return (
    <>
      <PageIntro
        title="Products"
        subtitle="Tailored lending solutions that empower our customers at every stage of progress."
        compactBottom
        contentWidthClass="max-w-[1400px]"
        bottomPadding="pb-4 md:pb-6"
      >
        <PageNav
          data={navData}
          allowedTitles={["Microcredit Loan", "Secured Business Loan (SBL)"]}
        />
      </PageIntro>
      <div className="bg-ofWhite">
        {data.page.map((section, index) => {
          const sectionId = slugify(section.title);
          return (
            <section
              key={section.title}
              id={sectionId}
              className={`${
                index === 0 ? "pt-4 sm:pt-6" : "pt-0"
              } pb-10 sm:pb-12`}
            >
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
                <ProductContent item={section} />

                {section.subsections && section.subsections.length > 0 && (
                  <div className="space-y-10">
                    {section.subsections.map((sub) => (
                      <div
                        key={sub.title}
                        id={`${sectionId}-${slugify(sub.title)}`}
                      >
                        <ProductContent item={sub} isSubsection />
                      </div>
                    ))}
                  </div>
                )}

                {section.extraSections && section.extraSections.length > 0 && (
                  <div className="space-y-8">
                    {section.extraSections.map((extra) => (
                      <div
                        key={extra.title}
                        id={`${sectionId}-${slugify(extra.title)}`}
                      >
                        <ExtraSection data={extra} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </>
  );
};

export default Products;
